import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import Cookie from 'js-cookie'
import { decodeJwt } from 'jose'

import { useApiStore } from './api'
import { useThemeStore } from './theme'

import { history } from '@/utils/app/history'
import { isAuthPage, isOrderPage } from '@/utils/auth'
import { api, cookies, urls } from '@/utils/constants'

import { AuthTokensShort } from '@/types/auth/tokens'
import { KonviveUser, User } from '@/types/user'
import { CreditCardCheckResponse } from '@/types/user/card'

const DEFAULT_KONVIVE: KonviveUser = {
    id: -1,
    firstName: '',
    credit: 0,
    cardId: -1,
    client: -1,
    site_customer: -1,
}

/** Verify if the token is still valid. */
const isValidToken = (token: string): boolean => {
    const now = new Date(Date.now())
    now.setMinutes(now.getMinutes() + 2)

    const dateNow = Math.ceil(now.getTime() / 1_000)

    try {
        const decodedToken = decodeJwt(token)

        if (!decodedToken.exp) {
            return false
        }

        return decodedToken.exp > dateNow
    } catch {
        return false
    }
}

type UserStore = {
    user: KonviveUser
    clientName: string | undefined
    isAskingAddCreditCard: boolean

    /** Initialise store user. */
    initialiseUser: () => Promise<void>

    /** Update the client name. */
    updateClientName: (clientName: string) => void

    /** Update is asking to add credit card. */
    updateIsAskingAddCreditCard: (newIsAskingAddCreditCard: boolean) => void

    /** Update credit card. */
    updateCredit: () => Promise<void>

    /** Update tokens. */
    updateTokens: () => Promise<void>
}

export const useUserStore = create<UserStore>()(
    immer((set, get) => ({
        user: DEFAULT_KONVIVE,

        clientName: undefined,

        isAskingAddCreditCard: false,

        initialiseUser: async () => {
            const { updateTokens } = get()
            const { requestWithoutHooks } = useApiStore.getState()
            const { updateTheme } = useThemeStore.getState()

            const path = new URL(window.location.href).pathname
            const isInAuthPage = isAuthPage()
            const isInOrderPage = isOrderPage()

            if (path.includes(urls.auth.ACTIVATION)) {
                return
            }

            await updateTokens()

            const accessToken = Cookie.get(cookies.tokens.ACCESS_TOKEN)

            const requestUser = await requestWithoutHooks
                .get(api.user.PROFILE, {
                    headers: {
                        Authorization: `JWT ${accessToken}`,
                    },
                })
                .json<User>()
                .catch(() => {
                    /* Empty */
                })

            if (!requestUser) {
                const [, client] = path.split('/')

                return await updateTheme(client)
            }

            set((state) => {
                state.user = {
                    id: requestUser[0].id,
                    credit: requestUser[0].credit,
                    firstName: requestUser[0].first_name,
                    ...(requestUser[0].card_id !== null && {
                        cardId: requestUser[0].card_id,
                    }),
                    ...(requestUser[0].client && {
                        client: requestUser[0].client.id,
                    }),
                    ...(requestUser[0].site_customer && {
                        site_customer: requestUser[0].site_customer.id,
                    }),
                }
            })

            const clientNameValue = requestUser[0].client.name
            const pathToSend = path === `/${clientNameValue}` ? '' : path

            await updateTheme(clientNameValue)

            set((state) => {
                state.clientName = clientNameValue
            })

            // User not logged and not in unlogged page
            if (!requestUser && !isInOrderPage) {
                return history.push(`/${clientNameValue}${urls.auth.LOGIN}`)
            }

            // User is logged, but in auth page
            if (requestUser && isInAuthPage) {
                return history.push(`/${clientNameValue}`)
            }

            // The path is not with the manager name
            if (!path.startsWith(`/${clientNameValue}`)) {
                history.push(`/${clientNameValue}${pathToSend}`)
            }

            const requestCreditCard = await requestWithoutHooks
                .get(api.user.CHECK_CREDIT_CARD, {
                    headers: {
                        Authorization: `JWT ${accessToken}`,
                    },
                })
                .json<CreditCardCheckResponse>()
                .catch(() => {
                    /* Empty */
                })

            if (!requestCreditCard) {
                return
            }

            if (!requestCreditCard.has_valid_credit_card) {
                set((state) => {
                    state.isAskingAddCreditCard = true
                })
            }
        },

        updateClientName: (newClientName: string) => {
            set((state) => {
                state.clientName = newClientName
            })
        },

        updateIsAskingAddCreditCard: (newIsAskingAddCreditCard) => {
            set((state) => {
                state.isAskingAddCreditCard = newIsAskingAddCreditCard
            })
        },

        updateCredit: async () => {
            const { requestWithoutHooks } = useApiStore.getState()
            await get().updateTokens()

            const accessToken = Cookie.get(cookies.tokens.ACCESS_TOKEN)

            const requestMe = await requestWithoutHooks
                .get(api.user.PROFILE, {
                    headers: {
                        Authorization: `JWT ${accessToken}`,
                    },
                })
                .json()
                .catch(() => {
                    /* Empty */
                })

            if (!requestMe) {
                return
            }

            set((state) => {
                state.user = {
                    ...state.user,
                    credit: requestMe[0].credit,
                }
            })
        },

        updateTokens: async () => {
            const { requestWithoutHooks } = useApiStore.getState()

            const accessToken = Cookie.get(cookies.tokens.ACCESS_TOKEN)
            const refreshToken = Cookie.get(cookies.tokens.REFRESH_TOKEN)

            const isInAuthPage = isAuthPage()
            const isInOrderPage = isOrderPage()

            if (!!accessToken && isValidToken(accessToken)) {
                return
            }

            if (
                !isInAuthPage &&
                !isInOrderPage &&
                (!refreshToken || !isValidToken(refreshToken))
            ) {
                return history.push(urls.auth.LOGIN)
            }

            const requestRefresh = await requestWithoutHooks
                .post(api.auth.REFRESH_TOKENS, {
                    json: {
                        refresh: refreshToken,
                    },
                })
                .json<AuthTokensShort>()
                .catch(() => {
                    /* Empty */
                })

            if (!requestRefresh) {
                return
            }

            Cookie.set(
                cookies.tokens.ACCESS_TOKEN,
                requestRefresh.access,
                cookies.tokens.OPTIONS,
            )

            Cookie.set(
                cookies.tokens.REFRESH_TOKEN,
                requestRefresh.refresh,
                cookies.tokens.OPTIONS,
            )
        },
    })),
)
